import React, { useState, useEffect, useRef } from "react";
import { Award, TrendingUp, Layers, MapPin } from "lucide-react";

const iconMap = { Award, TrendingUp, Layers, MapPin };

const useCounter = (end, inView, duration = 1800) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end, inView, duration]);
  return count;
};

const StatItem = ({ item, inView, index }) => {
  const value = parseInt(item.value) || 0;
  const count = useCounter(value, inView);
  const Icon = iconMap[item.icon] || Award;

  return (
    <div
      data-testid={`stat-item-${index}`}
      className={`flex flex-col items-center text-center px-8 py-4 scroll-hidden ${inView ? "scroll-visible" : ""}`}
      style={{ transitionDelay: `${index * 0.15}s` }}
    >
      <div className="w-14 h-14 rounded-full bg-[#ecc775]/15 flex items-center justify-center mb-4 border border-[#ecc775]/30">
        <Icon size={26} className="text-[#ecc775]" />
      </div>
      <div className="text-5xl font-black text-[#ecc775] leading-none mb-2" style={{ fontVariantNumeric: "tabular-nums" }}>
        {count}{item.suffix || ""}
      </div>
      <div className="text-white/85 font-semibold text-base">{item.label}</div>
    </div>
  );
};

const StatsBar = ({ stats }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const items = stats?.items || [
    { value: "3", suffix: "+", label: "سنوات الخبرة", icon: "Award" },
    { value: "200", suffix: "+", label: "صفقة ناجحة", icon: "TrendingUp" },
    { value: "6", suffix: "", label: "خدمة متكاملة", icon: "Layers" },
    { value: "1", suffix: "", label: "فرع في المملكة", icon: "MapPin" },
  ];

  return (
    <section
      ref={ref}
      data-testid="stats-bar"
      className="py-8"
      style={{ background: "linear-gradient(135deg, #0A1128 0%, #173269 50%, #0A1128 100%)" }}
    >
      {/* Gold top border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#ecc775] to-transparent mb-8"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {items.map((item, i) => (
            <div key={i} className={i < items.length - 1 ? "border-l border-[#ecc775]/20" : ""}>
              <StatItem item={item} inView={inView} index={i} />
            </div>
          ))}
        </div>
      </div>

      {/* Gold bottom border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#ecc775] to-transparent mt-8"></div>
    </section>
  );
};

export default StatsBar;
